import apiClient from '@/core/api/client';
import { API_ENDPOINTS } from '@/core/api/endpoints';

export interface NotificationItem {
    id: string;
    type: string;
    title: string;
    message: string;
    is_read: boolean;
    created_at: string;
    actor_name?: string | null;
    link?: string | null;
    [key: string]: any;
}

export interface NotificationListResponse {
    success: boolean;
    data?: any; // Could be Array or { items: [...] } — handled in caller
    message?: string | null;
    error?: string | null;
}

export interface NotificationResponse {
    success: boolean;
    data?: NotificationItem;
    message?: string | null;
    error?: string | null;
}

/**
 * Notifications API Service
 */
export const notificationsApi = {
    /**
     * Get all notifications for the current user in a school or global notifications for super admin
     */
    getNotifications: async (
        schoolSlug: string, 
        params: { page?: number; page_size?: number } = { page: 1, page_size: 10 }, 
        isSuperAdmin?: boolean
    ): Promise<NotificationListResponse> => {
        try {
            const url = isSuperAdmin
                ? API_ENDPOINTS.NOTIFICATIONS.GLOBAL_LIST
                : API_ENDPOINTS.NOTIFICATIONS.LIST(schoolSlug);
            const response = await apiClient.get<NotificationListResponse>(
                url,
                { params: { page: 1, page_size: 10, ...params } }
            );
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to load notifications'
            };
        }
    },

    /**
     * Mark a single notification as read
     */
    markAsRead: async (schoolSlug: string, notificationId: string, isSuperAdmin?: boolean): Promise<NotificationResponse> => {
        try {
            const url = isSuperAdmin
                ? API_ENDPOINTS.NOTIFICATIONS.GLOBAL_MARK_READ(notificationId)
                : API_ENDPOINTS.NOTIFICATIONS.MARK_READ(schoolSlug, notificationId);
            const response = await apiClient.patch<NotificationResponse>(url);
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to mark notification as read'
            };
        }
    },
};
